import { useEffect, useRef, useState, type FocusEvent, type Ref } from 'react';
import { Button } from '../../components/Button';
import { PriceDisplay } from '../../components/PriceDisplay';
import { BasketContents } from '../../composites/BasketContents';
import { DiscountCode } from '../../composites/DiscountCode';
import { Recommendations } from '../../composites/Recommendations';
import { useContainerHeight } from '../../hooks/useContainerHeight';
import { useInView } from '../../hooks/useInView';
import {
  BasketSection,
  DiscountColumn,
  DiscountRow,
  HeaderActions,
  HeaderActionsButton,
  HeaderBlock,
  PageWrapper,
  StickyBottomBar,
  StickyBottomBarInner,
  TotalHeading,
  TotalHeadingPrefix,
} from './BasketTemplate.styles';
import type { BasketTemplateProps } from './BasketTemplate.types';

export function BasketTemplate({
  items,
  totalPrice,
  view,
  onViewChange,
  guests,
  onRemoveItem,
  removingItemId = null,
  isUpdating = false,
  promoCode,
  voucherCode,
  subtotal,
  promotionsDiscount,
  vouchersDiscount,
  recommendations,
  onRecommendationSelect,
  emptyMessage,
  onContinueShopping,
  onCheckout,
  isCheckoutPending = false,
  isCheckoutBlocked = false,
  showAddedConfirmation = false,
  locale,
  labels,
}: BasketTemplateProps) {
  const isEmpty = items.length === 0;
  const topHeaderActionsRef = useRef<HTMLDivElement>(null);
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const isHeaderActionsInView = useInView(topHeaderActionsRef);
  const stickyBarHeight = useContainerHeight(stickyBarRef);
  const hasHeaderActions = !isEmpty && Boolean(onContinueShopping || onCheckout);
  const [topActionsHasFocus, setTopActionsHasFocus] = useState(false);
  const topActionsHidden = !isHeaderActionsInView && !topActionsHasFocus;
  const stickyActionsHidden = isHeaderActionsInView || topActionsHasFocus;

  const handleTopFocus = () => setTopActionsHasFocus(true);
  const handleTopBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocus = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(nextFocus)) setTopActionsHasFocus(false);
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (!hasHeaderActions || stickyBarHeight == null) {
      root.style.removeProperty('--page-sticky-bar-height');
      return;
    }
    root.style.setProperty('--page-sticky-bar-height', `${stickyBarHeight}px`);
    return () => {
      root.style.removeProperty('--page-sticky-bar-height');
    };
  }, [hasHeaderActions, stickyBarHeight]);

  const discountSlot = isEmpty ? undefined : (
    <DiscountRow>
      <DiscountColumn>
        <DiscountCode {...promoCode} />
      </DiscountColumn>
      <DiscountColumn>
        <DiscountCode {...voucherCode} />
      </DiscountColumn>
    </DiscountRow>
  );

  const renderHeaderActions = ({
    ref,
    hidden = false,
    onFocus,
    onBlur,
  }: {
    ref?: Ref<HTMLDivElement>;
    hidden?: boolean;
    onFocus?: (event: FocusEvent<HTMLDivElement>) => void;
    onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  } = {}) => {
    const buttonTabIndex = hidden ? -1 : undefined;
    return (
      <HeaderActions ref={ref} aria-hidden={hidden || undefined} onFocus={onFocus} onBlur={onBlur}>
        {onContinueShopping && (
          <HeaderActionsButton>
            <Button
              variant="neutral"
              fullWidth
              onClick={onContinueShopping}
              tabIndex={buttonTabIndex}
            >
              {labels.continueShopping}
            </Button>
          </HeaderActionsButton>
        )}
        {onCheckout && (
          <HeaderActionsButton>
            <Button
              variant="primary"
              fullWidth
              onClick={onCheckout}
              disabled={isCheckoutPending || isUpdating || isCheckoutBlocked}
              data-testid="clickCheckoutButton"
              tabIndex={buttonTabIndex}
            >
              {labels.checkout}
            </Button>
          </HeaderActionsButton>
        )}
      </HeaderActions>
    );
  };

  return (
    <PageWrapper data-testid="basket-template">
      <HeaderBlock>
        <TotalHeading>
          {isEmpty ? (
            labels.emptyHeading
          ) : (
            <>
              <TotalHeadingPrefix>{labels.totalHeadingPrefix}</TotalHeadingPrefix>
              <PriceDisplay
                amount={totalPrice.amount}
                currencyCode={totalPrice.currencyCode}
                locale={locale}
                size="xlarge"
                weight="medium"
              />
            </>
          )}
        </TotalHeading>
        {hasHeaderActions &&
          renderHeaderActions({
            ref: topHeaderActionsRef,
            hidden: topActionsHidden,
            onFocus: handleTopFocus,
            onBlur: handleTopBlur,
          })}
      </HeaderBlock>

      <BasketSection $empty={isEmpty}>
        <BasketContents
          items={items}
          totalPrice={totalPrice}
          view={view}
          onViewChange={onViewChange}
          guests={guests}
          showTitle={!isEmpty}
          showAddedConfirmation={showAddedConfirmation}
          onRemoveItem={onRemoveItem}
          removingItemId={removingItemId}
          isUpdating={isUpdating}
          emptyMessage={emptyMessage}
          discountSlot={discountSlot}
          subtotal={subtotal}
          promotionsDiscount={promotionsDiscount}
          vouchersDiscount={vouchersDiscount}
          locale={locale}
          labels={{
            title: labels.basketHeading,
            itemisedView: labels.itemisedView,
            itineraryView: labels.itineraryView,
            addedConfirmation: labels.addedConfirmation,
            subtotal: labels.subtotal,
            promotions: labels.promotions,
            vouchers: labels.vouchers,
            total: labels.total,
            removeLabel: labels.removeLabel,
            removing: labels.removing,
            noImageLabel: labels.noImageLabel,
            selectGuestLabel: labels.selectGuestLabel,
          }}
        />
        {isEmpty && onContinueShopping && (
          <HeaderActions $narrow>
            <Button variant="neutral" fullWidth onClick={onContinueShopping}>
              {labels.continueShopping}
            </Button>
          </HeaderActions>
        )}
      </BasketSection>

      <Recommendations
        items={recommendations}
        onSelect={onRecommendationSelect}
        title={labels.recommendationsHeadingPrimary}
        titleSecondary={labels.recommendationsHeadingSecondary}
        titleSize="large"
        fromPriceLabel={labels.fromPriceLabel}
        noImageLabel={labels.noImageLabel}
        layout="grid"
        columns={3}
        mobileColumns={2}
      />

      {hasHeaderActions && (
        <StickyBottomBar
          ref={stickyBarRef}
          $visible={!isHeaderActionsInView}
          aria-hidden={stickyActionsHidden}
        >
          <StickyBottomBarInner>
            {renderHeaderActions({ hidden: stickyActionsHidden })}
          </StickyBottomBarInner>
        </StickyBottomBar>
      )}
    </PageWrapper>
  );
}
